
import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface BookingData {
  date: string;
  count: number;
}

interface PieChartData {
  name: string;
  value: number;
}

export const AnalyticsCharts = () => {
  const [bookingData, setBookingData] = useState<BookingData[]>([]);
  const [vehicleTypeData, setVehicleTypeData] = useState<PieChartData[]>([]);
  const [timeRange, setTimeRange] = useState<string>("7");

  useEffect(() => {
    fetchBookingsData();
    fetchVehicleTypeDistribution();
  }, [timeRange]);

  const fetchBookingsData = async () => {
    try {
      const days = parseInt(timeRange);
      
      // Use our new RPC function
      const { data, error } = await supabase.rpc('get_bookings_by_day', { days_range: days });

      if (error) throw error;
      
      // Format data for chart (already sorted by date from the function)
      setBookingData(data || []);
    } catch (error) {
      console.error('Error fetching booking data:', error);
    }
  };

  const fetchVehicleTypeDistribution = async () => {
    try {
      // Use our new RPC function
      const { data, error } = await supabase.rpc('get_vehicle_type_distribution');

      if (error) throw error;
      
      // Set data for chart
      setVehicleTypeData(data || []);
    } catch (error) {
      console.error('Error fetching vehicle type distribution:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Booking Analytics</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Time Range:</span>
          <Select 
            value={timeRange} 
            onValueChange={setTimeRange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Daily Bookings</CardTitle>
            <CardDescription>Number of bookings per day</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <ChartContainer config={{ "bookings": { theme: { light: "#0284c7", dark: "#38bdf8" } } }} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bookingData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Legend />
                  <Bar dataKey="count" name="Bookings" fill="var(--color-bookings)" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vehicle Type Distribution</CardTitle>
            <CardDescription>Breakdown of parking spots by vehicle type</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <ChartContainer config={{ "vehicle": { theme: { light: "#0284c7", dark: "#38bdf8" } } }} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={vehicleTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {vehicleTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} spots`, 'Count']} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
